import { useNavigate } from 'react-router-dom'
import PublicLayout from '../components/layout/PublicLayout'
import { generateKey } from '../utils/key'

const FEATURES = [
  {
    icon: '🍔',
    title: 'AI nutrition lookup',
    body: 'Type a meal in plain language and Claude breaks it into items, each with a 26-field nutrition panel — calories, protein/carbs/fat, sugars, fat quality, fibre, sodium, cholesterol, potassium, alcohol, caffeine and core micronutrients. Confirm, adjust servings, and log.',
  },
  {
    icon: '🏃',
    title: 'Cardio',
    body: 'Log activity, level, gradient, duration, distance and calories. Calorie estimates and autocomplete from your own history make repeat entries instant.',
  },
  {
    icon: '🏋️',
    title: 'Weights',
    body: 'Track the lift, weight, reps and notes — quick to enter set after set, with personal-best context in your history.',
  },
  {
    icon: '🤸',
    title: 'General exercise',
    body: 'Anything else, captured with MET-based intensity so calories are estimated automatically from your body weight and duration.',
  },
  {
    icon: '🎯',
    title: 'Personalised targets',
    body: 'From your profile we compute BMI, BMR and TDEE (Mifflin–St Jeor), a goal-adjusted calorie target and protein g/kg, with micronutrient targets from AU/NZ reference values (NHMRC). Calorie and protein targets are overridable.',
  },
  {
    icon: '📊',
    title: 'Analytics & report',
    body: 'Overview, per-activity and nutrition tabs with charts for calories in vs out, energy balance and intake vs target, plus a daily move report. Tap any stat for a plain-language explainer.',
  },
  {
    icon: '🔑',
    title: 'Key-based access',
    body: 'No account or email required. Your log lives at a private random-words URL — bookmark it and it’s yours across devices.',
  },
  {
    icon: '🔒',
    title: 'Optional password lock',
    body: 'Add a password that locks on every visit or after inactivity. Server-side PBKDF2 hashing with per-IP rate limiting, and email-based reset if you forget it.',
  },
  {
    icon: '💾',
    title: 'Backups & export',
    body: 'Your last few versions are kept automatically and restorable in one tap. Export your whole log as JSON, or import one, whenever you like.',
  },
  {
    icon: '📭',
    title: 'Email recovery',
    body: 'Add a backup email and recover your key — or reset a forgotten password — by email. Authorised by your email, never by the key alone.',
  },
  {
    icon: '📶',
    title: 'Works offline',
    body: 'A local cache keeps your log readable when you’re offline; changes sync when you reconnect.',
  },
  {
    icon: '💸',
    title: 'Free to use',
    body: 'The whole tracker is free. A small Pro tier may arrive for heavy AI users to cover token costs — casual use stays free, always ad-free.',
  },
]

export default function Features() {
  const navigate = useNavigate()
  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-6 py-16">
        <p className="font-display text-sm font-bold uppercase tracking-widest text-[var(--accent-teal)]">Features</p>
        <h1 className="font-display mt-2 text-4xl font-extrabold leading-tight md:text-5xl">
          Everything you need. Nothing you don’t.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[var(--text-secondary)]">
          One fast, private place for what you move and what you eat.
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)]/60 p-5 backdrop-blur-sm">
              <div className="text-2xl">{f.icon}</div>
              <h2 className="font-display mt-3 text-lg font-bold">{f.title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button onClick={() => navigate(`/u/${generateKey()}?welcome=1`)} className="btn btn-primary px-8 py-3 text-base font-semibold">
            Start your log — free
          </button>
        </div>
      </div>
    </PublicLayout>
  )
}
